/**
 * KYC-skjema for klienter (offentlig side - ingen innlogging)
 * Klienten får tilsendt en link med token og fyller ut sine egne opplysninger
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';

export default function KYCSkjema({ token }) {
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [klientNavn, setKlientNavn] = useState('');
  const [tokenError, setTokenError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Skjemafelter
  const [formData, setFormData] = useState({
    fullt_navn: '',
    fødselsnummer: '',
    adresse: '',
    postnummer: '',
    poststed: '',
    telefon: '',
    epost: '',
    id_type: 'pass',
    id_nummer: '',
    id_gyldig_til: '',
    er_pep: false,
    er_nær_pep: false,
    pep_beskrivelse: '',
    er_selv_reell_rettighetshaver: true,
    reell_rettighetshaver_navn: '',
    reell_rettighetshaver_fnr: '',
    oppdrag_formål: '',
    sak_gjelder: '',
    midler_opprinnelse: []
  });

  // Valider token ved mount
  useEffect(() => {
    validateToken();
    // eslint-disable-next-line
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc/token/${token}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ugyldig token');
      }

      const data = await response.json();
      setTokenValid(true);
      setKlientNavn(data.klient_navn);
    } catch (error) {
      console.error('Token validering feilet:', error);
      setTokenError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullt_navn || !formData.fødselsnummer || !formData.adresse) {
      alert('Vennligst fyll ut alle obligatoriske felter');
      return;
    }

    if (formData.er_pep && !formData.pep_beskrivelse) {
      alert('Vennligst beskriv din politiske rolle/posisjon');
      return;
    }

    if (!formData.er_selv_reell_rettighetshaver && !formData.reell_rettighetshaver_navn) {
      alert('Vennligst oppgi hvem den reelle rettighetshaveren er');
      return;
    }

    if (formData.midler_opprinnelse.length === 0) {
      alert('Vennligst oppgi opprinnelse til midler');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc/token/${token}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          egenerklæring: formData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kunne ikke sende inn skjema');
      }

      const result = await response.json();
      setSubmitted(true);
      alert('✅ ' + result.message);
    } catch (error) {
      console.error('Innsending feilet:', error);
      alert('Innsending feilet: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMidlerToggle = (kilde) => {
    setFormData(prev => {
      const current = prev.midler_opprinnelse || [];
      if (current.includes(kilde)) {
        return { ...prev, midler_opprinnelse: current.filter(k => k !== kilde) };
      } else {
        return { ...prev, midler_opprinnelse: [...current, kilde] };
      }
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Validerer tilgang...</p>
      </div>
    );
  }

  if (tokenError || !tokenValid) {
    return (
      <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
        <h1 style={{ color: '#dc2626' }}>❌ Ugyldig eller utløpt lenke</h1>
        <p>{tokenError || 'Denne lenken er ikke lenger gyldig.'}</p>
        <p style={{ marginTop: '20px', color: '#6b7280' }}>
          Vennligst kontakt ditt advokatkontor for en ny lenke.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
        <h1 style={{ color: '#16a34a' }}>✅ Takk for din egenerklæring!</h1>
        <p>Dine opplysninger er mottatt og vil bli gjennomgått av advokat.</p>
        <p style={{ marginTop: '20px', color: '#6b7280' }}>
          Du vil bli kontaktet dersom vi trenger ytterligere informasjon.
        </p>
        <p style={{ marginTop: '30px', fontSize: '14px', color: '#9ca3af' }}>
          Du kan nå lukke denne siden.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ backgroundColor: '#f9fafb', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0' }}>📋 KYC-skjema</h1>
        <p style={{ margin: 0, color: '#6b7280' }}>
          {klientNavn && `Velkommen, ${klientNavn}. `}
          Vennligst fyll ut skjemaet under. Alle opplysninger behandles konfidensielt i henhold til GDPR.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Personopplysninger */}
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Personopplysninger</h2>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Fullt navn *
            </label>
            <input
              type="text"
              value={formData.fullt_navn}
              onChange={(e) => setFormData({ ...formData, fullt_navn: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Fødselsnummer (11 siffer) *
            </label>
            <input
              type="text"
              pattern="[0-9]{11}"
              value={formData.fødselsnummer}
              onChange={(e) => setFormData({ ...formData, fødselsnummer: e.target.value })}
              required
              maxLength="11"
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Adresse *
            </label>
            <input
              type="text"
              value={formData.adresse}
              onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Postnummer *
              </label>
              <input
                type="text"
                pattern="[0-9]{4}"
                value={formData.postnummer}
                onChange={(e) => setFormData({ ...formData, postnummer: e.target.value })}
                required
                maxLength="4"
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Poststed *
              </label>
              <input
                type="text"
                value={formData.poststed}
                onChange={(e) => setFormData({ ...formData, poststed: e.target.value })}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Telefon *
            </label>
            <input
              type="tel"
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              E-post *
            </label>
            <input
              type="email"
              value={formData.epost}
              onChange={(e) => setFormData({ ...formData, epost: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
        </section>

        {/* Identifikasjon */}
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Identifikasjonsdokument</h2>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Type dokument *
            </label>
            <select
              value={formData.id_type}
              onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="pass">Pass</option>
              <option value="førerkort">Førerkort</option>
              <option value="nasjonalt_id">Nasjonalt ID-kort</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Dokumentnummer *
            </label>
            <input
              type="text"
              value={formData.id_nummer}
              onChange={(e) => setFormData({ ...formData, id_nummer: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Gyldig til *
            </label>
            <input
              type="date"
              value={formData.id_gyldig_til}
              onChange={(e) => setFormData({ ...formData, id_gyldig_til: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
        </section>

        {/* PEP-status */}
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Politisk eksponert person (PEP)</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            En PEP er en person som har eller har hatt et fremtredende offentlig verv (f.eks. statsråd, stortingsrepresentant, direktør i statseide selskaper).
          </p>

          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.er_pep}
              onChange={(e) => setFormData({ ...formData, er_pep: e.target.checked })}
              style={{ marginRight: '8px', width: '18px', height: '18px' }}
            />
            <span>Jeg er eller har vært politisk eksponert</span>
          </label>

          {formData.er_pep && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Beskriv din rolle/posisjon *
              </label>
              <textarea
                value={formData.pep_beskrivelse}
                onChange={(e) => setFormData({ ...formData, pep_beskrivelse: e.target.value })}
                required
                rows="3"
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.er_nær_pep}
              onChange={(e) => setFormData({ ...formData, er_nær_pep: e.target.checked })}
              style={{ marginRight: '8px', width: '18px', height: '18px' }}
            />
            <span>Jeg er nært familiemedlem eller nær medarbeider til en PEP</span>
          </label>
        </section>

        {/* Reell rettighetshaver */}
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Reell rettighetshaver</h2>

          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.er_selv_reell_rettighetshaver}
              onChange={(e) => setFormData({ ...formData, er_selv_reell_rettighetshaver: e.target.checked })}
              style={{ marginRight: '8px', width: '18px', height: '18px' }}
            />
            <span>Jeg opptrer på egne vegne</span>
          </label>

          {!formData.er_selv_reell_rettighetshaver && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Navn på reell rettighetshaver *
                </label>
                <input
                  type="text"
                  value={formData.reell_rettighetshaver_navn}
                  onChange={(e) => setFormData({ ...formData, reell_rettighetshaver_navn: e.target.value })}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Fødselsnummer *
                </label>
                <input
                  type="text"
                  pattern="[0-9]{11}"
                  value={formData.reell_rettighetshaver_fnr}
                  onChange={(e) => setFormData({ ...formData, reell_rettighetshaver_fnr: e.target.value })}
                  required
                  maxLength="11"
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
            </>
          )}
        </section>

        {/* Oppdragets art */}
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Oppdragets art</h2>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Formål med oppdraget *
            </label>
            <textarea
              value={formData.oppdrag_formål}
              onChange={(e) => setFormData({ ...formData, oppdrag_formål: e.target.value })}
              required
              rows="3"
              placeholder="F.eks. juridisk bistand i kjøp av bolig"
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Hva saken gjelder
            </label>
            <textarea
              value={formData.sak_gjelder}
              onChange={(e) => setFormData({ ...formData, sak_gjelder: e.target.value })}
              rows="3"
              placeholder="Kort beskrivelse av hva saken dreier seg om"
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
        </section>

        {/* Midlenes opprinnelse */}
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Midlenes opprinnelse *</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            Velg alle kilder som er relevante for midlene som brukes i saken:
          </p>

          {['lønn', 'pensjon', 'arv', 'gave', 'salg_av_eiendom', 'salg_av_verdipapirer', 'lån', 'annet'].map(kilde => (
            <label key={kilde} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.midler_opprinnelse.includes(kilde)}
                onChange={() => handleMidlerToggle(kilde)}
                style={{ marginRight: '8px', width: '18px', height: '18px' }}
              />
              <span style={{ textTransform: 'capitalize' }}>{kilde.replace(/_/g, ' ')}</span>
            </label>
          ))}
        </section>

        {/* Submit */}
        <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            Ved å sende inn dette skjemaet bekrefter jeg at opplysningene jeg har gitt er korrekte etter mitt beste skjønn.
          </p>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: submitting ? '#9ca3af' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Sender inn...' : 'Send inn egenerklæring'}
          </button>
        </div>
      </form>

      <div style={{ marginTop: '30px', padding: '16px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '14px' }}>
        <strong>🔒 Personvern:</strong> Dine opplysninger behandles konfidensielt i henhold til GDPR og hvitvaskingsloven.
        Data krypteres og lagres sikkert hos Cloudflare.
      </div>
    </div>
  );
}
